import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { MultiSelect } from '@/components/ui/multiselect';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { useRightPanel } from '@/contexts/RightPanelContext';
import { SaveFilterModal } from './SaveFilterModal';
import { SavedFiltersDropdown } from './SavedFiltersDropdown';
import { toast } from 'sonner';
import { savedFiltersService } from '@/services/saved-filters-service';
import { dataService, isDemoMode } from '@/services/data-service';
import type { PendingFilters, SavedFilter } from '@/contexts/RightPanelContext';
import type { Location, Category, SubLocation, SubCategory } from '@/types/settings';

// Fallback mock data for dropdowns (used when API fails or in demo mode)
const FALLBACK_CATEGORIES = ['IT Equipment', 'Furniture', 'Vehicles', 'Machinery', 'Buildings', 'Land'];
const FALLBACK_STATUSES = [
  { label: 'Active', value: 'active', description: 'Asset is currently in use' },
  { label: 'Out of Service', value: 'outOfService', description: 'Temporarily not in use but still owned' },
  { label: 'Disposed', value: 'disposed', description: 'Asset has been sold, scrapped, or written off' },
  { label: 'Fully Depreciated', value: 'fullyDepreciated', description: 'Asset has reached end of useful life for accounting' },
  { label: 'Pending / On Order', value: 'pending', description: 'Asset has been purchased but not yet received' },
  { label: 'Under Maintenance / Repair', value: 'underMaintenance', description: 'Temporarily out of service for repairs' },
  { label: 'Reserved', value: 'reserved', description: 'Allocated for a specific purpose or project' },
  { label: 'Lost / Missing', value: 'lostMissing', description: 'Cannot be located' },
  { label: 'Leased', value: 'leased', description: 'Asset is leased rather than owned outright' },
];
const FALLBACK_LOCATIONS = ['Head Office', 'Warehouse A', 'Warehouse B', 'Branch 1', 'Branch 2', 'Remote'];
const ASSIGNED_TO = ['John Smith', 'Jane Doe', 'Bob Johnson', 'Alice Williams', 'Charlie Brown'];
const DEPRECIATION_METHODS = ['Straight Line', 'Declining Balance', 'Sum of Years Digits', 'Units of Production'];
const MANUFACTURERS = ['Dell', 'HP', 'Lenovo', 'Apple', 'Microsoft', 'Cisco', 'Samsung'];
const DEPARTMENTS = ['IT', 'Finance', 'HR', 'Operations', 'Sales', 'Marketing', 'Engineering', 'Administration'];

const getSubCategoryPlaceholder = (isLoading: boolean, optionsLength: number): string => {
  if (isLoading) return "Loading...";
  if (optionsLength === 0) return "No sub-categories available";
  return "Select sub-categories...";
};

const getSubLocationPlaceholder = (isLoading: boolean, optionsLength: number): string => {
  if (isLoading) return "Loading...";
  if (optionsLength === 0) return "No sub-locations available";
  return "Select sub-locations...";
};

const createEmptyFilters = (): Partial<PendingFilters> => ({
  selectedCategories: [],
  selectedStatuses: [],
  selectedLocations: [],
  acquisitionDateRange: { from: undefined, to: undefined },
  acquisitionCostMin: '',
  acquisitionCostMax: '',
  netBookValueMin: '',
  netBookValueMax: '',
  selectedAssignedTo: [],
  selectedDepreciationMethods: [],
  warrantyExpiryDateRange: { from: undefined, to: undefined },
  selectedManufacturers: [],
  selectedSubCategories: [],
  selectedSubLocations: [],
  assetCodeSearch: '',
  assetNameSearch: '',
  serialNumberSearch: '',
  descriptionSearch: '',
  modelSearch: '',
  selectedDepartments: [],
  usefulLifeMin: '',
  usefulLifeMax: '',
  insuranceExpiryDateRange: { from: undefined, to: undefined },
  barcodeSearch: '',
});

export const AssetFiltersTab: React.FC = () => {
  const { 
    setActiveFilterCount,
    pendingFilters,
    setPendingFilters,
    appliedFilters,
    applyFilters,
    clearAllFilters,
    savedFilters,
    setSavedFilters,
    loadSavedFilter,
  } = useRightPanel();

  // Local state for filter inputs
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedSubLocations, setSelectedSubLocations] = useState<string[]>([]);
  const [selectedSubCategories, setSelectedSubCategories] = useState<string[]>([]);
  const [acquisitionDateRange, setAcquisitionDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({ from: undefined, to: undefined });
  const [acquisitionCostMin, setAcquisitionCostMin] = useState('');
  const [acquisitionCostMax, setAcquisitionCostMax] = useState('');
  const [netBookValueMin, setNetBookValueMin] = useState('');
  const [netBookValueMax, setNetBookValueMax] = useState('');
  const [selectedAssignedTo, setSelectedAssignedTo] = useState<string[]>([]);
  const [selectedDepreciationMethods, setSelectedDepreciationMethods] = useState<string[]>([]);
  const [warrantyExpiryDateRange, setWarrantyExpiryDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({ from: undefined, to: undefined });
  const [selectedManufacturers, setSelectedManufacturers] = useState<string[]>([]);
  const [assetCodeSearch, setAssetCodeSearch] = useState('');
  const [assetNameSearch, setAssetNameSearch] = useState('');
  const [serialNumberSearch, setSerialNumberSearch] = useState('');
  const [descriptionSearch, setDescriptionSearch] = useState('');
  const [modelSearch, setModelSearch] = useState('');
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [usefulLifeMin, setUsefulLifeMin] = useState('');
  const [usefulLifeMax, setUsefulLifeMax] = useState('');
  const [insuranceExpiryDateRange, setInsuranceExpiryDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({ from: undefined, to: undefined });
  const [barcodeSearch, setBarcodeSearch] = useState('');

  // API-fetched filter options
  const [apiLocations, setApiLocations] = useState<Location[]>([]);
  const [apiCategories, setApiCategories] = useState<Category[]>([]);
  const [isLoadingFilterOptions, setIsLoadingFilterOptions] = useState(false);

  // Save filter modal state
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [editingFilter, setEditingFilter] = useState<SavedFilter | null>(null);

  // Selected saved filter
  const [selectedSavedFilterId, setSelectedSavedFilterId] = useState<string | null>(null);

  // Loading state for API operations
  const [, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch filter options from API on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      setIsLoadingFilterOptions(true);
      try {
        const [locations, categories] = await Promise.all([
          dataService.getLocations(),
          dataService.getCategories(),
        ]);
        setApiLocations(locations);
        setApiCategories(categories);
      } catch (error) {
        console.error('Failed to fetch filter options:', error);
      } finally {
        setIsLoadingFilterOptions(false);
      }
    };
    fetchFilterOptions();
  }, []);

  // Compute filter options from API data (only use fallback in demo mode)
  const locationOptions = useMemo(() => {
    if (apiLocations.length > 0) {
      return apiLocations.map(loc => ({ label: loc.name, value: loc.name }));
    }
    if (isDemoMode()) {
      return FALLBACK_LOCATIONS.map(loc => ({ label: loc, value: loc }));
    }
    return [];
  }, [apiLocations]);

  const categoryOptions = useMemo(() => {
    if (apiCategories.length > 0) {
      return apiCategories.map(cat => ({ label: cat.description ?? cat.code, value: cat.description ?? cat.code }));
    }
    if (isDemoMode()) {
      return FALLBACK_CATEGORIES.map(cat => ({ label: cat, value: cat }));
    }
    return [];
  }, [apiCategories]);

  const statusOptions = useMemo(() => {
    return FALLBACK_STATUSES.map(status => ({ 
      label: status.label,
      value: status.value,
      description: status.description,
    }));
  }, []);

  // Compute sub-location options from ALL locations (independent filter)
  const subLocationOptions = useMemo(() => {
    if (apiLocations.length === 0) {
      return [];
    }
    const subLocs: SubLocation[] = [];
    const uniqueSubLocs = new Set<string>();
    
    apiLocations.forEach(loc => {
      if (loc.subLocations) {
        loc.subLocations.forEach(subLoc => {
          if (!uniqueSubLocs.has(subLoc.name)) {
            uniqueSubLocs.add(subLoc.name);
            subLocs.push(subLoc);
          }
        });
      }
    });
    
    return subLocs.map(subLoc => ({ label: subLoc.name, value: subLoc.name }));
  }, [apiLocations]);

  // Compute sub-category options from ALL categories (independent filter)
  const subCategoryOptions = useMemo(() => {
    if (apiCategories.length === 0) {
      return [];
    }
    const subCats: SubCategory[] = [];
    const uniqueSubCats = new Set<string>();
    
    apiCategories.forEach(cat => {
      if (cat.subCategories) {
        cat.subCategories.forEach(subCat => {
          if (!uniqueSubCats.has(subCat.name)) {
            uniqueSubCats.add(subCat.name);
            subCats.push(subCat);
          }
        });
      }
    });
    
    return subCats.map(subCat => ({ label: subCat.name, value: subCat.name }));
  }, [apiCategories]);

  // Ref to track if we're loading from external source (to prevent circular updates)
  const isLoadingFromExternal = React.useRef(false);

  // Load saved filters from API on component mount
  const loadSavedFiltersFromApi = useCallback(async () => {
    setIsLoading(true);
    try {
      const filters = await savedFiltersService.getSavedFilters();
      setSavedFilters(filters);
    } catch (error) {
      console.error('Failed to load saved filters:', error);
      toast.error('Failed to load saved filters');
    } finally {
      setIsLoading(false);
    }
  }, [setSavedFilters]);

  // Load saved filters on mount
  useEffect(() => {
    loadSavedFiltersFromApi();
  }, [loadSavedFiltersFromApi]);

  // Update pending filters whenever local state changes (but not when loading from external)
  useEffect(() => {
    if (isLoadingFromExternal.current) {
      return;
    }
    
    const filters: Partial<PendingFilters> = {
      selectedCategories,
      selectedStatuses,
      selectedLocations,
      selectedSubLocations,
      selectedSubCategories,
      acquisitionDateRange,
      acquisitionCostMin,
      acquisitionCostMax,
      netBookValueMin,
      netBookValueMax,
      selectedAssignedTo,
      selectedDepreciationMethods,
      warrantyExpiryDateRange,
      selectedManufacturers,
      assetCodeSearch,
      assetNameSearch,
      serialNumberSearch,
      descriptionSearch,
      modelSearch,
      selectedDepartments,
      usefulLifeMin,
      usefulLifeMax,
      insuranceExpiryDateRange,
      barcodeSearch,
    };
    setPendingFilters(filters as PendingFilters);
  }, [
    selectedCategories,
    selectedStatuses,
    selectedLocations,
    selectedSubLocations,
    selectedSubCategories,
    acquisitionDateRange,
    acquisitionCostMin,
    acquisitionCostMax,
    netBookValueMin,
    netBookValueMax,
    selectedAssignedTo,
    selectedDepreciationMethods,
    warrantyExpiryDateRange,
    selectedManufacturers,
    assetCodeSearch,
    assetNameSearch,
    serialNumberSearch,
    descriptionSearch,
    modelSearch,
    selectedDepartments,
    usefulLifeMin,
    usefulLifeMax,
    insuranceExpiryDateRange,
    barcodeSearch,
    setPendingFilters,
  ]);

  // Load pending filters into local state when they change from external source
  useEffect(() => {
    if (pendingFilters) {
      isLoadingFromExternal.current = true;
      setSelectedCategories(pendingFilters.selectedCategories);
      setSelectedStatuses(pendingFilters.selectedStatuses);
      setSelectedLocations(pendingFilters.selectedLocations);
      setSelectedSubLocations(pendingFilters.selectedSubLocations ?? []);
      setSelectedSubCategories(pendingFilters.selectedSubCategories ?? []);
      setAcquisitionDateRange(pendingFilters.acquisitionDateRange);
      setAcquisitionCostMin(pendingFilters.acquisitionCostMin);
      setAcquisitionCostMax(pendingFilters.acquisitionCostMax);
      setNetBookValueMin(pendingFilters.netBookValueMin);
      setNetBookValueMax(pendingFilters.netBookValueMax);
      setSelectedAssignedTo(pendingFilters.selectedAssignedTo);
      setSelectedDepreciationMethods(pendingFilters.selectedDepreciationMethods);
      setWarrantyExpiryDateRange(pendingFilters.warrantyExpiryDateRange);
      setSelectedManufacturers(pendingFilters.selectedManufacturers);
      setAssetCodeSearch(pendingFilters.assetCodeSearch ?? '');
      setAssetNameSearch(pendingFilters.assetNameSearch ?? '');
      setSerialNumberSearch(pendingFilters.serialNumberSearch ?? '');
      setDescriptionSearch(pendingFilters.descriptionSearch ?? '');
      setModelSearch(pendingFilters.modelSearch ?? '');
      setSelectedDepartments(pendingFilters.selectedDepartments ?? []);
      setUsefulLifeMin(pendingFilters.usefulLifeMin ?? '');
      setUsefulLifeMax(pendingFilters.usefulLifeMax ?? '');
      setInsuranceExpiryDateRange(pendingFilters.insuranceExpiryDateRange ?? { from: undefined, to: undefined });
      setBarcodeSearch(pendingFilters.barcodeSearch ?? '');
      // Reset flag after state updates are done
      setTimeout(() => {
        isLoadingFromExternal.current = false;
      }, 0);
    }
  }, [pendingFilters]);

  // Clear local state when both pending and applied filters are cleared (e.g., from "Clear All" button in header)
  useEffect(() => {
    if (!pendingFilters && !appliedFilters) {
      isLoadingFromExternal.current = true;
      const emptyFilters = createEmptyFilters();
      setSelectedCategories(emptyFilters.selectedCategories ?? []);
      setSelectedStatuses(emptyFilters.selectedStatuses ?? []);
      setSelectedLocations(emptyFilters.selectedLocations ?? []);
      setSelectedSubLocations(emptyFilters.selectedSubLocations ?? []);
      setSelectedSubCategories(emptyFilters.selectedSubCategories ?? []);
      setAcquisitionDateRange(emptyFilters.acquisitionDateRange ?? { from: undefined, to: undefined });
      setAcquisitionCostMin(emptyFilters.acquisitionCostMin ?? '');
      setAcquisitionCostMax(emptyFilters.acquisitionCostMax ?? '');
      setNetBookValueMin(emptyFilters.netBookValueMin ?? '');
      setNetBookValueMax(emptyFilters.netBookValueMax ?? '');
      setSelectedAssignedTo(emptyFilters.selectedAssignedTo ?? []);
      setSelectedDepreciationMethods(emptyFilters.selectedDepreciationMethods ?? []);
      setWarrantyExpiryDateRange(emptyFilters.warrantyExpiryDateRange ?? { from: undefined, to: undefined });
      setSelectedManufacturers(emptyFilters.selectedManufacturers ?? []);
      setAssetCodeSearch(emptyFilters.assetCodeSearch ?? '');
      setAssetNameSearch(emptyFilters.assetNameSearch ?? '');
      setSerialNumberSearch(emptyFilters.serialNumberSearch ?? '');
      setDescriptionSearch(emptyFilters.descriptionSearch ?? '');
      setModelSearch(emptyFilters.modelSearch ?? '');
      setSelectedDepartments(emptyFilters.selectedDepartments ?? []);
      setUsefulLifeMin(emptyFilters.usefulLifeMin ?? '');
      setUsefulLifeMax(emptyFilters.usefulLifeMax ?? '');
      setInsuranceExpiryDateRange(emptyFilters.insuranceExpiryDateRange ?? { from: undefined, to: undefined });
      setBarcodeSearch(emptyFilters.barcodeSearch ?? '');
      setSelectedSavedFilterId(null);
      // Reset flag after state updates are done
      setTimeout(() => {
        isLoadingFromExternal.current = false;
      }, 0);
    }
  }, [pendingFilters, appliedFilters]);

  const handleClearAll = () => {
    isLoadingFromExternal.current = true;
    const emptyFilters = createEmptyFilters();
    setSelectedCategories(emptyFilters.selectedCategories ?? []);
    setSelectedStatuses(emptyFilters.selectedStatuses ?? []);
    setSelectedLocations(emptyFilters.selectedLocations ?? []);
    setSelectedSubLocations(emptyFilters.selectedSubLocations ?? []);
    setSelectedSubCategories(emptyFilters.selectedSubCategories ?? []);
    setAcquisitionDateRange(emptyFilters.acquisitionDateRange ?? { from: undefined, to: undefined });
    setAcquisitionCostMin(emptyFilters.acquisitionCostMin ?? '');
    setAcquisitionCostMax(emptyFilters.acquisitionCostMax ?? '');
    setNetBookValueMin(emptyFilters.netBookValueMin ?? '');
    setNetBookValueMax(emptyFilters.netBookValueMax ?? '');
    setSelectedAssignedTo(emptyFilters.selectedAssignedTo ?? []);
    setSelectedDepreciationMethods(emptyFilters.selectedDepreciationMethods ?? []);
    setWarrantyExpiryDateRange(emptyFilters.warrantyExpiryDateRange ?? { from: undefined, to: undefined });
    setSelectedManufacturers(emptyFilters.selectedManufacturers ?? []);
    setAssetCodeSearch(emptyFilters.assetCodeSearch ?? '');
    setAssetNameSearch(emptyFilters.assetNameSearch ?? '');
    setSerialNumberSearch(emptyFilters.serialNumberSearch ?? '');
    setDescriptionSearch(emptyFilters.descriptionSearch ?? '');
    setModelSearch(emptyFilters.modelSearch ?? '');
    setSelectedDepartments(emptyFilters.selectedDepartments ?? []);
    setUsefulLifeMin(emptyFilters.usefulLifeMin ?? '');
    setUsefulLifeMax(emptyFilters.usefulLifeMax ?? '');
    setInsuranceExpiryDateRange(emptyFilters.insuranceExpiryDateRange ?? { from: undefined, to: undefined });
    setBarcodeSearch(emptyFilters.barcodeSearch ?? '');
    setSelectedSavedFilterId(null);
    clearAllFilters(); // Clear both pending and applied filters in context
    // Reset flag after state updates are done
    setTimeout(() => {
      isLoadingFromExternal.current = false;
    }, 0);
  };

  const activeFilterCount = 
    selectedCategories.length +
    selectedSubCategories.length +
    selectedStatuses.length +
    selectedLocations.length +
    selectedSubLocations.length +
    (acquisitionDateRange.from || acquisitionDateRange.to ? 1 : 0) +
    (acquisitionCostMin || acquisitionCostMax ? 1 : 0) +
    (netBookValueMin || netBookValueMax ? 1 : 0) +
    selectedAssignedTo.length +
    selectedDepreciationMethods.length +
    (warrantyExpiryDateRange.from || warrantyExpiryDateRange.to ? 1 : 0) +
    selectedManufacturers.length +
    (assetCodeSearch ? 1 : 0) +
    (assetNameSearch ? 1 : 0) +
    (serialNumberSearch ? 1 : 0) +
    (descriptionSearch ? 1 : 0) +
    (modelSearch ? 1 : 0) +
    selectedDepartments.length +
    (usefulLifeMin || usefulLifeMax ? 1 : 0) +
    (insuranceExpiryDateRange.from || insuranceExpiryDateRange.to ? 1 : 0) +
    (barcodeSearch ? 1 : 0);

  // Update the context with the active filter count
  useEffect(() => {
    setActiveFilterCount(activeFilterCount);
  }, [activeFilterCount, setActiveFilterCount]);

  // Listen for external clear all filters (from header button)
  useEffect(() => {
    // If both applied and pending filters are null but we have local filter state,
    // it means clearAllFilters was called from outside (e.g., header button)
    if (appliedFilters === null && pendingFilters === null && activeFilterCount > 0 && !isLoadingFromExternal.current) {
      // Clear all was called from outside, reset local state without triggering context clear again
      const emptyFilters = createEmptyFilters();
      setSelectedCategories(emptyFilters.selectedCategories ?? []);
      setSelectedStatuses(emptyFilters.selectedStatuses ?? []);
      setSelectedLocations(emptyFilters.selectedLocations ?? []);
      setSelectedSubLocations(emptyFilters.selectedSubLocations ?? []);
      setSelectedSubCategories(emptyFilters.selectedSubCategories ?? []);
      setAcquisitionDateRange(emptyFilters.acquisitionDateRange ?? { from: undefined, to: undefined });
      setAcquisitionCostMin(emptyFilters.acquisitionCostMin ?? '');
      setAcquisitionCostMax(emptyFilters.acquisitionCostMax ?? '');
      setNetBookValueMin(emptyFilters.netBookValueMin ?? '');
      setNetBookValueMax(emptyFilters.netBookValueMax ?? '');
      setSelectedAssignedTo(emptyFilters.selectedAssignedTo ?? []);
      setSelectedDepreciationMethods(emptyFilters.selectedDepreciationMethods ?? []);
      setWarrantyExpiryDateRange(emptyFilters.warrantyExpiryDateRange ?? { from: undefined, to: undefined });
      setSelectedManufacturers(emptyFilters.selectedManufacturers ?? []);
      setAssetCodeSearch(emptyFilters.assetCodeSearch ?? '');
      setAssetNameSearch(emptyFilters.assetNameSearch ?? '');
      setSerialNumberSearch(emptyFilters.serialNumberSearch ?? '');
      setDescriptionSearch(emptyFilters.descriptionSearch ?? '');
      setModelSearch(emptyFilters.modelSearch ?? '');
      setSelectedDepartments(emptyFilters.selectedDepartments ?? []);
      setUsefulLifeMin(emptyFilters.usefulLifeMin ?? '');
      setUsefulLifeMax(emptyFilters.usefulLifeMax ?? '');
      setInsuranceExpiryDateRange(emptyFilters.insuranceExpiryDateRange ?? { from: undefined, to: undefined });
      setBarcodeSearch(emptyFilters.barcodeSearch ?? '');
      setSelectedSavedFilterId(null);
    }
  }, [appliedFilters, pendingFilters, activeFilterCount]);

  const hasFilterChanges = () => {
    // No changes if both are empty
    if (!appliedFilters && activeFilterCount === 0) return false;
    
    // Changes if we have filters but nothing was applied before
    if (!appliedFilters && activeFilterCount > 0) return true;
    
    // Changes if we had applied filters but now have none (user cleared all)
    if (appliedFilters && activeFilterCount === 0) return true;
    
    // No pending filters to compare
    if (!pendingFilters) return false;

    // Deep comparison
    return JSON.stringify(pendingFilters) !== JSON.stringify(appliedFilters);
  };

  const handleApplyFilters = () => {
    applyFilters();
  };

  const handleSaveAndApply = () => {
    if (activeFilterCount === 0) {
      toast.error('Please configure at least one filter before saving');
      return;
    }
    setSaveModalOpen(true);
  };

  const handleSaveFilter = async (name: string) => {
    if (!pendingFilters) return;

    setIsSaving(true);
    try {
      if (editingFilter) {
        // Update existing filter via API
        const updatedFilter = await savedFiltersService.updateSavedFilter(
          editingFilter.id,
          name,
          pendingFilters
        );
        if (updatedFilter) {
          const updatedFilters = savedFilters.map((filter) =>
            filter.id === editingFilter.id ? updatedFilter : filter
          );
          setSavedFilters(updatedFilters);
          toast.success(`Filter '${name}' updated successfully`);
        } else {
          toast.error('Filter not found');
        }
        setEditingFilter(null);
      } else {
        // Create new filter via API
        const newFilter = await savedFiltersService.createSavedFilter(name, pendingFilters);
        setSavedFilters([...savedFilters, newFilter]);
        toast.success(`Filter '${name}' saved successfully`);
        setSelectedSavedFilterId(newFilter.id);
      }

      // Apply the filters
      applyFilters();
    } catch (error) {
      console.error('Failed to save filter:', error);
      toast.error('Failed to save filter');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectSavedFilter = (filterId: string | null) => {
    setSelectedSavedFilterId(filterId);
    if (filterId) {
      loadSavedFilter(filterId);
    } else {
      // Clear selection - show "Custom" state
      handleClearAll();
    }
  };

  const handleEditFilter = (filter: SavedFilter) => {
    setEditingFilter(filter);
    setSaveModalOpen(true);
  };

  const handleDeleteFilter = async (filterId: string) => {
    try {
      const success = await savedFiltersService.deleteSavedFilter(filterId);
      if (success) {
        setSavedFilters(savedFilters.filter((f) => f.id !== filterId));
        if (selectedSavedFilterId === filterId) {
          setSelectedSavedFilterId(null);
        }
        toast.success('Filter deleted successfully');
      } else {
        toast.error('Filter not found');
      }
    } catch (error) {
      console.error('Failed to delete filter:', error);
      toast.error('Failed to delete filter');
    }
  };

  const handleDuplicateFilter = async (filter: SavedFilter) => {
    try {
      const duplicatedFilter = await savedFiltersService.createSavedFilter(
        `${filter.name} (Copy)`,
        filter.filters
      );
      setSavedFilters([...savedFilters, duplicatedFilter]);
      toast.success(`Filter '${duplicatedFilter.name}' created`);
      setSelectedSavedFilterId(duplicatedFilter.id);
      loadSavedFilter(duplicatedFilter.id);
    } catch (error) {
      console.error('Failed to duplicate filter:', error);
      toast.error('Failed to duplicate filter');
    }
  };

  // Detect if current filters match a saved filter
  useEffect(() => {
    if (!pendingFilters || activeFilterCount === 0) {
      if (selectedSavedFilterId) {
        setSelectedSavedFilterId(null);
      }
      return;
    }

    // Check if current filters match the selected saved filter
    if (selectedSavedFilterId) {
      const savedFilter = savedFilters.find((f) => f.id === selectedSavedFilterId);
      if (savedFilter && JSON.stringify(savedFilter.filters) !== JSON.stringify(pendingFilters)) {
        // Filters have been modified, clear selection to show "Custom"
        setSelectedSavedFilterId(null);
      }
    }
  }, [pendingFilters, activeFilterCount, selectedSavedFilterId, savedFilters]);

  const hasUnsavedChanges = hasFilterChanges();

  return (
    <div className="flex flex-col h-full">
      {/* Saved Filters Dropdown - Full Width, Outside Padding */}
      <div className="border-b bg-[#f8fafc] dark:bg-[#0f172b]" style={{ borderColor: '#eee' }}>
        <SavedFiltersDropdown
          savedFilters={savedFilters}
          selectedFilterId={selectedSavedFilterId}
          onSelectFilter={handleSelectSavedFilter}
          onEditFilter={handleEditFilter}
          onDeleteFilter={handleDeleteFilter}
          onDuplicateFilter={handleDuplicateFilter}
        />
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 pb-24">

        {/* Category Filter */}
        <div className="space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Category</Label>
              {selectedCategories.length > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {selectedCategories.length}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Assets by their category type</p>
          </div>

          <MultiSelect
            options={categoryOptions}
            value={selectedCategories}
            onValueChange={setSelectedCategories}
            placeholder={isLoadingFilterOptions ? "Loading..." : "Select categories..."}
          />
        </div>

        <Separator />

        {/* Sub-Category Filter */}
        <div className="space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Sub-Category</Label>
              {selectedSubCategories.length > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {selectedSubCategories.length}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Filter by specific sub-category</p>
          </div>

          <MultiSelect
            options={subCategoryOptions}
            value={selectedSubCategories}
            onValueChange={setSelectedSubCategories}
            placeholder={getSubCategoryPlaceholder(isLoadingFilterOptions, subCategoryOptions.length)}
          />
        </div>

        <Separator />

        {/* Status Filter */}
        <div className="space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Status</Label>
              {selectedStatuses.length > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {selectedStatuses.length}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Asset lifecycle status</p>
          </div>

          <MultiSelect
            options={statusOptions}
            value={selectedStatuses}
            onValueChange={setSelectedStatuses}
            placeholder="Select statuses..."
          />
        </div>

        <Separator />

        {/* Location Filter */}
        <div className="space-y-2">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Location</Label>
              {selectedLocations.length > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {selectedLocations.length}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Physical location or site</p>
          </div>

          <MultiSelect
            options={locationOptions}
            value={selectedLocations}
            onValueChange={setSelectedLocations}
            placeholder={isLoadingFilterOptions ? "Loading..." : "Select locations..."}
          />
        </div>

        <Separator />

        {/* Sub-Location Filter */}
        <div className="space-y-2">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Sub-Location</Label>
              {selectedSubLocations.length > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {selectedSubLocations.length}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Filter by specific sub-location</p>
          </div>

          <MultiSelect
            options={subLocationOptions}
            value={selectedSubLocations}
            onValueChange={setSelectedSubLocations}
            placeholder={getSubLocationPlaceholder(isLoadingFilterOptions, subLocationOptions.length)}
          />
        </div>

        <Separator />

        {/* Acquisition Date Range Filter */}
        <div className="space-y-2">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Acquisition Date</Label>
              {(acquisitionDateRange.from || acquisitionDateRange.to) && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  1
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Date when asset was acquired</p>
          </div>

          <DateRangePicker
            dateRange={acquisitionDateRange}
            onDateRangeChange={(range) => setAcquisitionDateRange({ from: range?.from, to: range?.to })}
          />
        </div>

        <Separator />

        {/* Acquisition Cost Range Filter */}
        <div className="space-y-2">
          <div className="space-y-1">
            <Label className="text-sm font-semibold">Acquisition Cost</Label>
            <p className="text-xs text-muted-foreground">Original purchase price range</p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="cost-min" className="text-xs text-muted-foreground">Min</Label>
              <Input
                id="cost-min"
                type="number"
                placeholder="0"
                value={acquisitionCostMin}
                onChange={(e) => setAcquisitionCostMin(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cost-max" className="text-xs text-muted-foreground">Max</Label>
              <Input
                id="cost-max"
                type="number"
                placeholder="999999"
                value={acquisitionCostMax}
                onChange={(e) => setAcquisitionCostMax(e.target.value)}
                className="h-9"
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Net Book Value Range Filter */}
        <div className="space-y-2">
          <div className="space-y-1">
            <Label className="text-sm font-semibold">Net Book Value</Label>
            <p className="text-xs text-muted-foreground">Current value after depreciation</p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="nbv-min" className="text-xs text-muted-foreground">Min</Label>
              <Input
                id="nbv-min"
                type="number"
                placeholder="0"
                value={netBookValueMin}
                onChange={(e) => setNetBookValueMin(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="nbv-max" className="text-xs text-muted-foreground">Max</Label>
              <Input
                id="nbv-max"
                type="number"
                placeholder="999999"
                value={netBookValueMax}
                onChange={(e) => setNetBookValueMax(e.target.value)}
                className="h-9"
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Assigned To Filter */}
        <div className="space-y-2">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Assigned To</Label>
              {selectedAssignedTo.length > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {selectedAssignedTo.length}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Person or user assigned to asset</p>
          </div>

          <MultiSelect
            options={ASSIGNED_TO.map(person => ({ label: person, value: person }))}
            value={selectedAssignedTo}
            onValueChange={setSelectedAssignedTo}
            placeholder="Select assignees..."
          />
        </div>

        <Separator />

        {/* Depreciation Method Filter */}
        <div className="space-y-2">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Depreciation Method</Label>
              {selectedDepreciationMethods.length > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {selectedDepreciationMethods.length}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Depreciation calculation method</p>
          </div>

          <MultiSelect
            options={DEPRECIATION_METHODS.map(method => ({ label: method, value: method }))}
            value={selectedDepreciationMethods}
            onValueChange={setSelectedDepreciationMethods}
            placeholder="Select methods..."
          />
        </div>

        <Separator />

        {/* Warranty Expiry Date Range Filter */}
        <div className="space-y-2">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Warranty Expiry Date</Label>
              {(warrantyExpiryDateRange.from || warrantyExpiryDateRange.to) && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  1
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Warranty expiration date range</p>
          </div>

          <DateRangePicker
            dateRange={warrantyExpiryDateRange}
            onDateRangeChange={(range) => setWarrantyExpiryDateRange({ from: range?.from, to: range?.to })}
          />
        </div>

        <Separator />

        {/* Manufacturer Filter */}
        <div className="space-y-2">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Manufacturer</Label>
              {selectedManufacturers.length > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {selectedManufacturers.length}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Asset manufacturer or brand</p>
          </div>

          <MultiSelect
            options={MANUFACTURERS.map(mfr => ({ label: mfr, value: mfr }))}
            value={selectedManufacturers}
            onValueChange={setSelectedManufacturers}
            placeholder="Select manufacturers..."
          />
        </div>

        <Separator />

        {/* Asset Code Search Filter */}
        <div className="space-y-2">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Asset Code</Label>
              {assetCodeSearch && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  1
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Search by asset code</p>
          </div>

          <Input
            type="text"
            placeholder="Enter asset code..."
            value={assetCodeSearch}
            onChange={(e) => setAssetCodeSearch(e.target.value)}
            className="h-9"
          />
        </div>

        <Separator />

        {/* Asset Name Search Filter */}
        <div className="space-y-2">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Asset Name</Label>
              {assetNameSearch && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  1
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Search by asset name</p>
          </div>

          <Input
            type="text"
            placeholder="Enter asset name..."
            value={assetNameSearch}
            onChange={(e) => setAssetNameSearch(e.target.value)}
            className="h-9"
          />
        </div>

        <Separator />

        {/* Serial Number Search Filter */}
        <div className="space-y-2">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Serial Number</Label>
              {serialNumberSearch && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  1
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Search by serial number</p>
          </div>

          <Input
            type="text"
            placeholder="Enter serial number..."
            value={serialNumberSearch}
            onChange={(e) => setSerialNumberSearch(e.target.value)}
            className="h-9"
          />
        </div>

        <Separator />

        {/* Description Search Filter */}
        <div className="space-y-2">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Description</Label>
              {descriptionSearch && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  1
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Search by asset description</p>
          </div>

          <Input
            type="text"
            placeholder="Enter description..."
            value={descriptionSearch}
            onChange={(e) => setDescriptionSearch(e.target.value)}
            className="h-9"
          />
        </div>

        <Separator />

        {/* Model Search Filter */}
        <div className="space-y-2">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Model</Label>
              {modelSearch && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  1
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Search by model name</p>
          </div>

          <Input
            type="text"
            placeholder="Enter model..."
            value={modelSearch}
            onChange={(e) => setModelSearch(e.target.value)}
            className="h-9"
          />
        </div>

        <Separator />

        {/* Department Filter */}
        <div className="space-y-2">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Department</Label>
              {selectedDepartments.length > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {selectedDepartments.length}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Filter by department</p>
          </div>

          <MultiSelect
            options={DEPARTMENTS.map(dept => ({ label: dept, value: dept }))}
            value={selectedDepartments}
            onValueChange={setSelectedDepartments}
            placeholder="Select departments..."
          />
        </div>

        <Separator />

        {/* Useful Life Range Filter */}
        <div className="space-y-2">
          <div className="space-y-1">
            <Label className="text-sm font-semibold">Useful Life (Years)</Label>
            <p className="text-xs text-muted-foreground">Asset useful life range</p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="useful-life-min" className="text-xs text-muted-foreground">Min</Label>
              <Input
                id="useful-life-min"
                type="number"
                placeholder="0"
                value={usefulLifeMin}
                onChange={(e) => setUsefulLifeMin(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="useful-life-max" className="text-xs text-muted-foreground">Max</Label>
              <Input
                id="useful-life-max"
                type="number"
                placeholder="100"
                value={usefulLifeMax}
                onChange={(e) => setUsefulLifeMax(e.target.value)}
                className="h-9"
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Insurance Expiry Date Range Filter */}
        <div className="space-y-2">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Insurance Expiry Date</Label>
              {(insuranceExpiryDateRange.from || insuranceExpiryDateRange.to) && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  1
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Insurance expiration date range</p>
          </div>

          <DateRangePicker
            dateRange={insuranceExpiryDateRange}
            onDateRangeChange={(range) => setInsuranceExpiryDateRange({ from: range?.from, to: range?.to })}
          />
        </div>

        <Separator />

        {/* Barcode Search Filter */}
        <div className="space-y-2">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Barcode</Label>
              {barcodeSearch && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  1
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Search by barcode value</p>
          </div>

          <Input
            type="text"
            placeholder="Enter barcode..."
            value={barcodeSearch}
            onChange={(e) => setBarcodeSearch(e.target.value)}
            className="h-9"
          />
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="sticky bottom-0 bg-background border-t shadow-lg p-4">
        {hasUnsavedChanges && (
          <div className="mb-3 text-center">
            <Badge variant="secondary" className="text-xs">
              Unsaved changes
            </Badge>
          </div>
        )}
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleSaveAndApply}
            disabled={activeFilterCount === 0 || isSaving}
            className="flex-1"
          >
            {isSaving ? 'Saving...' : 'Save and Apply'}
          </Button>
          <Button
            onClick={handleApplyFilters}
            disabled={!hasUnsavedChanges}
            className="flex-1"
          >
            Apply Filter
          </Button>
        </div>
      </div>

      {/* Save Filter Modal */}
      {pendingFilters && (
        <SaveFilterModal
          open={saveModalOpen}
          onOpenChange={setSaveModalOpen}
          pendingFilters={pendingFilters}
          existingFilters={savedFilters}
          onSave={handleSaveFilter}
          editingFilter={editingFilter}
        />
      )}
    </div>
  );
};
